import React, { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { getSecurityStatus, type SecurityStatus } from '@/api/pinSecurityApi'
import FirstTimeSetup from './FirstTimeSetup'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

interface SecuritySetupGuardProps {
  children: React.ReactNode
}

/**
 * Component that checks if user needs first-time security setup
 * and shows the setup flow if needed, otherwise renders children
 */
const SecuritySetupGuard: React.FC<SecuritySetupGuardProps> = ({ children }) => {
  const { user } = useAuth()
  const [securityStatus, setSecurityStatus] = useState<SecurityStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [needsSetup, setNeedsSetup] = useState(false)

  useEffect(() => {
    if (user) {
      checkSecurityStatus()
    }
  }, [user])

  const checkSecurityStatus = async () => {
    try {
      setLoading(true)
      const response = await getSecurityStatus()
      setSecurityStatus(response.data)
      
      // Determine if user needs setup
      const requiresSetup = response.data.isFirstLogin && (!response.data.hasPIN || !response.data.emailVerified)
      setNeedsSetup(requiresSetup)
      
      console.log('Security status check:', {
        isFirstLogin: response.data.isFirstLogin,
        hasPIN: response.data.hasPIN,
        emailVerified: response.data.emailVerified,
        requiresSetup
      })
      
    } catch (error: any) {
      console.error('Failed to check security status:', error)
      // On error, assume setup is not needed to avoid blocking access
      setNeedsSetup(false)
    } finally {
      setLoading(false)
    }
  }

  // FirstTimeSetup component now handles navigation internally

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">Checking security status...</p>
        </div>
      </div>
    )
  }

  // Show first-time setup if needed
  if (needsSetup) {
    return <FirstTimeSetup />
  }

  // Render normal app content
  return <>{children}</>
}

export default SecuritySetupGuard